/**
 * Codec seam for {@link GopDecoder}.
 *
 * Isolates the per-codec bitstream knowledge — keyframe detection and the
 * WebCodecs codec-string derivation — behind one interface, so the GOP/batch
 * decoding logic stays codec-agnostic and depends only on a `VideoCodec`.
 * H.264, H.265, VP9, and AV1 are registered; a new codec adds a `VideoCodec`
 * implementation plus one entry in {@link CODECS}, with no change to the decoder.
 */

import { av1 } from "./av1";
import { h264 } from "./h264";
import { h265 } from "./h265";
import { UnsupportedCodecError } from "./VideoDecoderInterface";
import { vp9 } from "./vp9";

export interface VideoCodec {
  /** Per-message `format` tokens that resolve to this codec (compared lowercase). */
  readonly formats: readonly string[];
  /**
   * How this codec's frames are framed for WebCodecs: `"annexb"` for
   * start-code-delimited NAL streams (H.264/H.265), `"raw"` for one
   * self-contained frame/OBU per chunk (VP9/AV1).
   */
  readonly framing: "annexb" | "raw";
  /**
   * A broadly-supported codec string for the up-front "can this platform
   * decode this codec at all?" probe, before any stream bytes are seen.
   */
  readonly probeCodecString: string;
  /** Whether an encoded frame is an independently decodable keyframe. */
  isKeyframe(frame: Uint8Array): boolean;
  /**
   * The WebCodecs codec string derived from a keyframe (e.g. `"avc1.4d401e"`),
   * or `undefined` when the keyframe carries no usable decoder configuration.
   */
  codecStringFromKeyframe(frame: Uint8Array): string | undefined;
}

const CODECS: readonly VideoCodec[] = [h264, h265, vp9, av1];

const byFormat = new Map<string, VideoCodec>(
  CODECS.flatMap((codec) =>
    codec.formats.map((format) => [format, codec] as const),
  ),
);

/**
 * The codec registered for a per-message `format` token (matched
 * case-insensitively), or `undefined` when no codec has a decode path for it.
 */
export function resolveCodec(format: string): VideoCodec | undefined {
  return byFormat.get(format.toLowerCase());
}

/**
 * The codec for an encoded frame's `format` token: H.264 when the token is
 * absent (streams predating the per-message codec declaration are H.264).
 *
 * @throws {UnsupportedCodecError} when the token names a codec with no
 * decode path — failing by name beats feeding bytes to the wrong decoder.
 */
export function resolveFrameCodec(format: string | undefined): VideoCodec {
  if (format === undefined) {
    return h264;
  }
  const codec = resolveCodec(format);
  if (codec === undefined) {
    throw new UnsupportedCodecError(format);
  }
  return codec;
}

/** The per-message `format` tokens that have a decode path, lowercase. */
export function supportedVideoFormats(): string[] {
  return [...byFormat.keys()];
}
